import React, { useEffect, useState } from "react";

import { Button } from "@app/scripts/react-general-components/Button";
import { Drawer } from "@app/scripts/react-general-components/Drawer";
import { useShopify, useShopifyProduct } from "@app/scripts/react-hooks/shopify";
import { shopify } from "@app/scripts/utils/graphql";
import { GET_PRODUCTS_BY_FILTER } from "@app/scripts/utils/queries";
import { ProductMoreColorsProduct } from "@app/scripts/react-product-more-colors/components/ProductMoreColorsProduct";

import type { NormalisedProduct, Product } from "@app/scripts/types";

type Props = {
  product: Product;
};

export const ProductMoreColorsContent = ({ product }: { product: NormalisedProduct }) => {
  const { normaliseProduct } = useShopify();
  const { color, title: productTitle } = useShopifyProduct({ product });
  const [related, setRelated] = useState<NormalisedProduct[]>([]);
  const [loaded, setLoaded] = useState(false);

  const title = `${productTitle}: ${related?.length} colours`;
  const btnTitle = `View all ${related?.length} colours`;

  const cropFromTop = /short|pant|hat/.test(product.productType?.toLowerCase() || "");

  useEffect(() => {
    const query = async () => {
      // "style--" is a legacy tag for backwards compatibility purposes, can be removed post 08/2025
      const styleTag = product?.tags.find((tag) => tag.includes("altCode--") || tag.includes("style--"));
      if (!styleTag) return;

      const data = await shopify(GET_PRODUCTS_BY_FILTER, {
        filter: "tag:" + styleTag,
        limit: 100,
      });

      setRelated(data?.products?.nodes?.map((p) => normaliseProduct(p) || []));
      setLoaded(true);
    };
    query();
  }, []);

  return (
    <>
      <div className="product__flex">
        <span className="!text-lg product__color-tag">
          <strong className="font-medium">{`Colour: `}</strong> {color}
        </span>

        {loaded && related?.length > 1 && (
          <Drawer
            heading={title}
            opensFrom={"bottom"}
            drawerClassName="product-more-colors__drawer"
            customButton={({ toggleOpen }) => (
              <Button
                variant="text"
                className="swiper-no-swiping product-more-colors__button"
                ariaLabel={btnTitle}
                onClick={toggleOpen}
                label={btnTitle}
              />
            )}
          >
            <div className="product-more-colors page-width">
              {related?.map((r_product) => (
                <ProductMoreColorsProduct key={r_product.id} product={r_product} parentProduct={product} />
              ))}
            </div>
          </Drawer>
        )}
      </div>

      {!loaded && (
        <div className="product__related">
          <item-carousel
            columns-desktop="6"
            columns-mobile="4"
            disabled-mobile="false"
            disabled-desktop="false"
            scrollbar-mobile="true"
            small-btns="true"
          >
            <div className={`item-carousel product__related_item-carousel ${cropFromTop ? "crop-from-top" : ""}`}>
              {Array.apply(null, Array(10)).map((_) => (
                <div className="product__related_item-carousel__image" />
              ))}
            </div>
          </item-carousel>
        </div>
      )}

      {loaded && related?.length > 0 && (
        <div className="product__related">
          <item-carousel
            columns-desktop="6"
            columns-mobile="4"
            disabled-mobile="false"
            disabled-desktop="false"
            scrollbar-mobile="true"
            small-btns="true"
          >
            <div className="item-carousel product__related_item-carousel">
              {related.map((r_product) => {
                const isCurrentProduct = r_product.id.includes(product.id);
                return (
                  <a
                    href={`/products/${r_product.handle}`}
                    key={r_product.id}
                    className={`image-container ${isCurrentProduct ? "active-border" : ""}`}
                  >
                    {r_product.media?.[0]?.src ? (
                      <img
                        srcSet={`${r_product.media?.[0].src}&width=70 70w,
                        ${r_product.media?.[0].src}&width=150 150w,
                        ${r_product.media?.[0].src}&width=202 202w`}
                        src={`${r_product.media?.[0].src}&width=70}`}
                        alt={r_product.media?.[0].alt}
                        sizes="(min-width: 2600px) 150px, (min-width: 990px) 70px, (min-width: 750px) 150px, (min-width: 600px) 202px, 70px"
                        className="product__related_item-carousel__image"
                        loading="lazy"
                      />
                    ) : (
                      <div className="product__related_item-carousel__image" />
                    )}
                  </a>
                );
              })}
            </div>
          </item-carousel>
        </div>
      )}
    </>
  );
};

export const ProductMoreColors: React.FC<Props> = ({ product }) => {
  const { normaliseProduct } = useShopify();
  const normalised = normaliseProduct(product);
  return <ProductMoreColorsContent product={normalised} />;
};
